import type { Register } from '../profile/register';
import type { Mark } from './transcript.ts';

/**
 * The one language the cast can be heard in.
 *
 * Every character's `tts_voice` is a Kokoro English voice, and Kokoro's voice
 * list (probed on the running server, 2026-09-17) covers English, Spanish,
 * French, Hindi, Italian, Japanese, Portuguese and Chinese -- and none of the
 * other nineteen languages the profile offers, Vietnamese among them. Handing
 * Vietnamese text to `af_heart` does not fail; it reads the letters as if they
 * were English, which is worse than offering nothing because the learner
 * cannot tell it is wrong.
 *
 * A single name rather than a list of what Kokoro can manage: the cast is
 * entirely English-voiced, so English is the only language any character can
 * currently say, whatever the model could do with a voice we do not use.
 */
const SPOKEN_LANGUAGE = 'English';

/**
 * Whether a stored line in `language` can be read aloud to the learner.
 *
 * Exported because `AidsPanel` asks it of a translation's own
 * `Translation.language`, and this rule belongs beside `aidsFor` rather than
 * in the markup -- the same reason `aidsFor` exists at all.
 */
export function isSpeakable(language: string | null): boolean {
  return language === SPOKEN_LANGUAGE;
}

/** What a line can be asked for. */
export type Aid = 'translate' | 'speak' | 'sayInEnglish';

/**
 * Which aids a transcript line offers.
 *
 * A pure rule with tests rather than a condition in the markup, for the same
 * reason the debrief's register gate is: §8.2 forbids showing a child their own
 * errors, and a gate that lives in JSX is one someone re-derives slightly
 * differently the next time a surface needs it.
 *
 * Note what is *not* conditional. §4.1 puts translation on both speakers in
 * both registers: translating what was said is not naming an error. The kid
 * register loses nothing here — what §8.2 bites on is the corrections in
 * `Turn.errors`, not the words themselves.
 */
export function aidsFor({
  who,
  hasNativeLanguage,
  translationTarget,
}: {
  who: 'learner' | 'tutor' | 'error';
  register: Register;
  hasNativeLanguage: boolean;
  /**
   * What `translateTurn` will put this line into, exactly as the server's
   * `Turn.translationTarget` field (or the live `LearnerTurnCommitted`/
   * `TutorTurnCommitted` event) reports it: `"English"` on the one learner
   * line §5 fires the offer for, the learner's native language otherwise,
   * `null` when there is nothing to translate into at all.
   *
   * This is the server's answer, not a value computed here. An earlier
   * version of this function re-derived it from `Turn.language` and the
   * profile's native language with its own name/code lookup
   * (`languageCodeFor`), which disagreed with the server's own answer for
   * three of the profile's twenty-seven languages — `Chinese (Mandarin)`,
   * `Filipino` and `Punjabi` — because isolang's `display_name` does not
   * round-trip a parenthetical qualifier or resolves a code to a different
   * English name than the dropdown's. A Mandarin speaker's Mandarin turn was
   * offered "Say this in English" and labelled as such while the server kept
   * translating it into Chinese. Reading the server's own value instead of
   * re-deriving it is what makes that class of bug impossible here rather
   * than merely fixed for those three languages.
   */
  translationTarget: string | null;
}): Aid[] {
  // An error line is this screen's own message about a failure, not something
  // anybody said. There is nothing to translate and nothing to hear.
  if (who === 'error') return [];

  const aids: Aid[] = [];
  if (hasNativeLanguage) aids.push('translate');

  // A line is offered aloud unless it is the learner's own first language.
  //
  // `translationTarget === SPOKEN_LANGUAGE` is the server saying it will
  // translate this line *into* English, which it only does for a learner turn
  // it detected in their native language (§5). So that one value is also the
  // evidence that the line itself is not English -- and no character has a
  // voice that could say it. The two halves are exact complements: the line
  // that cannot be heard is precisely the line whose translation can, which is
  // why `AidsPanel` gates its own "Hear it" the other way round.
  //
  // `null` keeps the button. That is a learner with no native language
  // recorded, where nothing here is evidence of anything, and the reading
  // before this rule existed was to offer it.
  if (translationTarget !== SPOKEN_LANGUAGE) aids.push('speak');

  // §5: a learner falling back to their first language is the clearest
  // evidence the system ever gets that they could not say something in
  // English. `who === 'learner'` still guards this even though a tutor
  // line's `translationTarget` could coincidentally be `"English"` (a
  // learner who typed "English" as their own native language, say) --
  // that is not evidence of anything the *tutor* did.
  if (who === 'learner' && translationTarget === 'English') {
    aids.push('sayInEnglish');
  }

  return aids;
}

/** A correction a line can offer to be heard: the record's id and what it says. */
export type HearableCorrection = { id: string; targetForm: string };

/**
 * Which corrections a line's aids panel offers to say aloud.
 *
 * Aids PRD §4.2 hears an `ErrorRecord` by id, never by string. Adult only,
 * and only on the learner's own line: the kid transcript marks nothing
 * (§8.2), and a correction offered where it was never shown would be the
 * mark by another route.
 */
export function correctionsFor({
  who,
  register,
  marks,
}: {
  who: 'learner' | 'tutor' | 'error';
  register: Register;
  marks: readonly Mark[] | undefined;
}): HearableCorrection[] {
  if (who !== 'learner' || register !== 'adult') return [];
  return (marks ?? []).map((m) => ({ id: m.id, targetForm: m.targetForm }));
}
